/**
 * Push notifications for native iOS/Android (Capacitor).
 * Uses APNs (iOS) / FCM (Android). No-op on web.
 */

import { Capacitor } from '@capacitor/core';

export type NativePermissionState = 'granted' | 'denied' | 'prompt';

export function isNativePushSupported(): boolean {
  return Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';
}

export async function getNativePermissionStatus(): Promise<NativePermissionState> {
  if (!isNativePushSupported()) return 'denied';
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const result = await PushNotifications.checkPermissions();
    return (result.receive as NativePermissionState) ?? 'prompt';
  } catch {
    return 'prompt';
  }
}

export async function requestNativePermission(): Promise<boolean> {
  if (!isNativePushSupported()) return false;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const result = await PushNotifications.requestPermissions();
    if (result.receive === 'granted') {
      await PushNotifications.register();
      return true;
    }
    return false;
  } catch (err) {
    console.warn('Push permission request failed:', err);
    return false;
  }
}

/**
 * Opens the app's notification settings in the system Settings app.
 * On iOS: opens Settings → Luma → Notifications (or app settings on older iOS).
 * On Android: opens app notification settings.
 * No-op on web.
 */
export async function openAppNotificationSettings(): Promise<boolean> {
  if (!isNativePushSupported()) return false;
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.getPlatform() === 'ios') {
      const { NativeSettings, IOSSettings } = await import('capacitor-native-settings');
      const result = await NativeSettings.openIOS({
        option: IOSSettings.AppNotification,
      });
      return result.status;
    }
    if (Capacitor.getPlatform() === 'android') {
      const { NativeSettings, AndroidSettings } = await import('capacitor-native-settings');
      const result = await NativeSettings.openAndroid({
        option: AndroidSettings.AppNotification,
      });
      return result.status;
    }
    return false;
  } catch (err) {
    console.warn('Failed to open app notification settings:', err);
    return false;
  }
}

/**
 * Register for push when permission already granted (e.g. on app resume).
 * Does NOT prompt - use requestNativePermission() when user taps "Enable".
 */
export function registerPushIfNative(): void {
  if (Capacitor.getPlatform() === 'web') return;

  import('@capacitor/push-notifications')
    .then(async ({ PushNotifications }) => {
      const status = await PushNotifications.checkPermissions();
      if (status.receive === 'granted') {
        await PushNotifications.register();
      }

      PushNotifications.addListener(
        'registration',
        (token) => {
          // Send token to your backend (e.g. Supabase) so you can target this device for push
          console.info('Push registration token:', token.value);
        }
      );
      PushNotifications.addListener('registrationError', (err) => console.warn('Push registration error', err));
    })
    .catch(() => {
      // @capacitor/push-notifications not installed or load failed
    });
}
